"use client";

import React from "react";
import { CircleAlert, Loader, CircleCheck, User, Mail, Phone, Home, MessageSquare, ChevronDown } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const propertyTypeOptions = ["Single-Family Home", "Duplex", "Multi-Family (2-4 units)", "Apartment Complex", "Other"];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <CircleCheck className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif font-semibold text-foreground">Request Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto text-lg">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" size="lg" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  const inputStyles = "h-12 bg-white border-border shadow-md rounded-lg pl-10 focus-visible:ring-primary/20";
  const iconStyles = "absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters required"
        },
        maxLength: {
          value: 100,
          message: "Maximum 100 characters allowed"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1">
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className={iconStyles} />
                  <Input placeholder="John Doe" className={inputStyles} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1">
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className={iconStyles} />
                  <Input type="email" placeholder="john@example.com" className={inputStyles} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className={iconStyles} />
                  <Input type="tel" placeholder="(816) 555-1234" className={inputStyles} {...field} />
                </div>
              </FormControl>
              <FormDescription className="text-muted-foreground/70">
                We'll use this to contact you about your consultation.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_type" rules={{
        required: "Please select a property type"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-foreground font-medium">Property Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <Home className={iconStyles} />
                  <select className={cn(inputStyles, "appearance-none w-full pr-10 bg-white cursor-pointer")} {...field}>
                    <option value="" disabled>
                      Select property type
                    </option>
                    {propertyTypeOptions.map((option, i) => <option key={option} value={option} data-index={i}>
                        {option}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70 pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="details" render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-foreground font-medium">Brief Details (optional)</FormLabel>
              <FormControl>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-4 size-4 text-muted-foreground/70" />
                  <Textarea placeholder="Tell us about your property or any specific needs..." className="min-h-[120px] bg-white border-border shadow-md rounded-lg pl-10 pt-3 focus-visible:ring-primary/20" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <Alert variant="destructive" className="rounded-lg">
          <CircleAlert className="size-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold shadow-lg bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting Request...
          </> : "Submit Request"}
      </Button>
    </Form>;
}
export default function StartYourConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="start-your-consultation" className="relative py-24 lg:py-32 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: "repeating-linear-gradient(to right, var(--color-foreground) 0, var(--color-foreground) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(to bottom, var(--color-foreground) 0, var(--color-foreground) 1px, transparent 1px, transparent 40px)"
    }} aria-hidden="true" />
      
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />

      <div ref={ref} className={cn("container mx-auto px-4 max-w-[800px] relative z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary tracking-tight">
            Start Your Consultation
          </h2>
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
            Partner with Invest KC Property Management to maximize your rental property's potential. Fill out the form below to get started.
          </p>
        </div>

        <Alert className="mb-10 border-primary/20 bg-primary/5 shadow-sm rounded-lg">
          <CircleAlert className="text-primary" />
          <AlertTitle className="text-primary font-semibold">High Demand Notice</AlertTitle>
          <AlertDescription className="text-foreground/80">
            Due to current market conditions in the Kansas City area, we are experiencing a high volume of consultation requests. Please provide as much detail as possible so we can best prepare for our initial conversation.
          </AlertDescription>
        </Alert>

        <div className="bg-card/50 backdrop-blur-sm border border-border/40 rounded-2xl p-6 md:p-12 shadow-2xl shadow-shadow-color/5">
          <FormProvider formId="consultation_form_home" sectionName="Start Your Consultation" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          property_type: "",
          details: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          property_type: {
            required: true
          }
        }} submitText="Submit Request" successMessage="Thank you for reaching out! We have received your consultation request and a member of our team will contact you within 1 business day.">
            <ConsultationFormInner />
          </FormProvider>
        </div>
      </div>
    </section>;
}